const PAB_SURCHARGE = 10;
const mobilityLinePricingModes = new Set(['flatPerLine', 'firstLineAdditional', 'totalLineCountTier']);

const numberOrFallback = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const formatMoney = (amount) => (
    Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`
);

export function normalizeMobilityLinePricing(plan = {}, fallbackPlan = {}) {
    const source = plan.linePricing || fallbackPlan.linePricing || {};
    const fallbackPrice = numberOrFallback(plan.price, fallbackPlan.price ?? 0);
    let mode = mobilityLinePricingModes.has(source.mode) ? source.mode : '';

    if (!mode) {
        if (plan.id === 'telus-personal-canada-100gb-ffh' || fallbackPlan.id === 'telus-personal-canada-100gb-ffh') {
            mode = 'totalLineCountTier';
        } else if (plan.id === 'telus-epp-rbc-175gb' || fallbackPlan.id === 'telus-epp-rbc-175gb') {
            mode = 'firstLineAdditional';
        } else {
            mode = 'flatPerLine';
        }
    }

    const tiers = Array.isArray(source.tiers)
        ? source.tiers.map((tier) => ({
            minLines: Math.max(1, Number(tier.minLines) || 1),
            maxLines: tier.maxLines === null || tier.maxLines === undefined || tier.maxLines === ''
                ? null
                : Math.max(1, Number(tier.maxLines) || 1),
            pricePerLine: numberOrFallback(tier.pricePerLine, fallbackPrice),
            label: tier.label || ''
        })).sort((left, right) => left.minLines - right.minLines)
        : [];

    if (mode === 'totalLineCountTier' && tiers.length === 0) {
        if (plan.id === 'telus-personal-canada-100gb-ffh' || fallbackPlan.id === 'telus-personal-canada-100gb-ffh') {
            tiers.push(
                { minLines: 1, maxLines: 3, pricePerLine: 45, label: '1-3 lines' },
                { minLines: 4, maxLines: 4, pricePerLine: 41.25, label: '4 lines' }
            );
        } else {
            tiers.push({ minLines: 1, maxLines: null, pricePerLine: fallbackPrice, label: 'All lines' });
        }
    }

    return {
        mode,
        flatPricePerLine: numberOrFallback(source.flatPricePerLine, fallbackPrice),
        firstLinePrice: numberOrFallback(
            source.firstLinePrice,
            plan.id === 'telus-epp-rbc-175gb' || fallbackPlan.id === 'telus-epp-rbc-175gb' ? 40 : fallbackPrice
        ),
        additionalLinePrice: numberOrFallback(
            source.additionalLinePrice,
            plan.id === 'telus-epp-rbc-175gb' || fallbackPlan.id === 'telus-epp-rbc-175gb' ? 35 : fallbackPrice
        ),
        tiers,
        maxLines: source.maxLines === null || source.maxLines === undefined || source.maxLines === ''
            ? null
            : Math.max(1, Number(source.maxLines) || 1),
        noteTemplate: source.noteTemplate || ''
    };
}

export function calculateMobilityLinePricingTotal(linePricing, lines, fallbackPrice = 0) {
    const safeLines = Math.max(1, Number(lines) || 1);
    const pricing = normalizeMobilityLinePricing({ linePricing, price: fallbackPrice });

    if (pricing.mode === 'firstLineAdditional') {
        return pricing.firstLinePrice + Math.max(0, safeLines - 1) * pricing.additionalLinePrice;
    }

    if (pricing.mode === 'totalLineCountTier') {
        const matchingTier = pricing.tiers.find((tier) => (
            safeLines >= tier.minLines && (tier.maxLines === null || safeLines <= tier.maxLines)
        )) || pricing.tiers[pricing.tiers.length - 1];

        return safeLines * numberOrFallback(matchingTier?.pricePerLine, fallbackPrice);
    }

    return safeLines * pricing.flatPricePerLine;
}

export function getMobilityTotal(plan, lines, pabIncluded = true) {
    if (!plan) return 0;

    const safeLines = Math.max(1, lines);
    const pabAdjustment = pabIncluded ? 0 : safeLines * PAB_SURCHARGE;
    const linePricing = normalizeMobilityLinePricing(plan);
    const baseTotal = calculateMobilityLinePricingTotal(linePricing, safeLines, plan.price);
    return baseTotal + pabAdjustment;
}

export function formatMobilityLinePricingNote(linePricing) {
    if (!linePricing) return '';
    if (linePricing.noteTemplate) return linePricing.noteTemplate;

    if (linePricing.mode === 'firstLineAdditional') {
        return `First line ${formatMoney(linePricing.firstLinePrice)}; second and rest ${formatMoney(linePricing.additionalLinePrice)}.`;
    }

    if (linePricing.mode === 'totalLineCountTier') {
        return (linePricing.tiers || []).map((tier) => {
            const range = tier.maxLines === null || tier.maxLines === undefined
                ? `${tier.minLines}+ lines`
                : tier.minLines === tier.maxLines
                    ? `${tier.minLines} line${tier.minLines === 1 ? '' : 's'}`
                    : `${tier.minLines}-${tier.maxLines} lines`;
            return `${formatMoney(tier.pricePerLine)}/line for ${range}`;
        }).join('; ') + '.';
    }

    return `${formatMoney(linePricing.flatPricePerLine)}/line.`;
}
